import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';
import Footer from '../components/Footer';

// Pricing page with dynamic product data from API
const PricingPage: React.FC = () => {
  const { products: apiProducts } = useProducts();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [heroImage, setHeroImage] = useState<string>('');
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});
  const [consumerAffairsLogo, setConsumerAffairsLogo] = useState<string>('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  useEffect(() => {
    // Fetch images from backend
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then((data: any) => {
        // Look for pricing hero image - 225MedicalAlertSystemInHomeFallDetection
        const hero = data.images.find((img: any) => 
          img.filename.toLowerCase().includes('225medicalalertsysteminhomefalldetection') ||
          img.filename.toLowerCase().includes('225medical')
        );
        if (hero) {
          setHeroImage(`${API_URL}/api/images/${hero.id}`);
        }

        // Use same images as dropdown menu
        // Get product images from API products
        const imageMap: {[key: string]: string} = {};
        apiProducts.forEach(product => {
          if (product.imageFilename) {
            const img = data.images.find((i: any) => i.filename === product.imageFilename);
            if (img) {
              imageMap[product.shortName] = `${API_URL}/api/images/${img.id}`;
            }
          }
        });
        setProductImages(imageMap);

        // Look for Consumer Affairs logo
        const consumerAffairs = data.images.find((img: any) => 
          img.filename.toLowerCase().includes('consumeraffairs') ||
          img.filename.toLowerCase().includes('consumer-affairs') ||
          img.filename.toLowerCase().includes('consumer_affairs') ||
          img.filename.toLowerCase().includes('image 10-9-25 at 1.46 pm') ||
          img.filename.toLowerCase().includes('image10-9-25')
        );
        if (consumerAffairs) {
          setConsumerAffairsLogo(`${API_URL}/api/images/${consumerAffairs.id}`);
        }
      })
      .catch(err => console.error('Error fetching images:', err));
  }, [API_URL, apiProducts]);

  return (
    <div className="pricing-page">
      {/* Hero Section with Image and Text */}
      <section className="pricing-hero-section">
        <div className="pricing-hero-container">
          <h1 className="pricing-hero-main-title">Low Cost, High Value Medical Alert Systems</h1>
          
          <div className="pricing-hero-content-wrapper">
            <div className="pricing-hero-image">
              {heroImage ? (
                <img src={heroImage} alt="Medical Alert Customer" className="pricing-hero-img" />
              ) : (
                <div className="pricing-hero-placeholder">
                  <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                    <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                    <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                    <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                  </svg>
                </div>
              )}
            </div>
            
            <div className="pricing-hero-text">
              <h2 className="pricing-hero-title">Call us for our Lowest Price Guarantee!</h2>
              
              <p className="pricing-hero-paragraph">
                Don't pay too much for your med alert! Alert1 does not charge for equipment or calls to the monitoring center. We offer free activation, free equipment use, no contracts, and no hidden fees.
              </p>
              
              <p className="pricing-hero-paragraph">
                We also have one of the lowest monthly costs for 24/7 emergency monitoring service. Our competitors charge between $29 for an in home unit to $149 a month for an on the go device, often with additional equipment purchases and high activation fees.
              </p>
              
              <p className="pricing-hero-paragraph">
                Apples to apples, compare medical alert systems and you'll find that Alert1's low prices on personal emergency response systems are <strong>up to 50% cheaper</strong> than our competitors' rates.
              </p>
              
              <p className="pricing-hero-paragraph">
                Also, while other companies hold you to multi-year contracts, you can sign up for an Alert 1 senior safety system or fall alert device with no long term contract required. Any user may cancel service at any time by returning the equipment.<sup>2</sup> We make the process of getting security and peace of mind for you and your loved ones smooth, simple, and straightforward.
              </p>
              
              <p className="pricing-hero-paragraph">
                Think having the cheapest prices means lower quality? Not with Alert1! We offer all the newest emergency help alert pendants and wristbands. Our Command Centers are 5 Diamond Certified. World-class service is our daily goal. We've been saving lives for over 37 years for a reason. So check out our low cost med alert devices for in-home or mobile use and discover the affordable emergency button alert system seniors and elderly adults love best. Whether you choose an alert wristband, help pendant, or automatic fall detection system, <strong>you win</strong>.
              </p>
              
              <p className="pricing-hero-link">
                <a href="/medical-alerts">Click here to start shopping now.</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Device Section */}
      <section id="product-tiles" className="pricing-choose-device-section">
        <div className="pricing-choose-device-container">
          <h2 className="pricing-choose-device-title">Choose your perfect medical alert device.</h2>
          
          <div className="pricing-device-grid">
            {apiProducts
              .filter(p => p.isActive && !p.shortName.includes('-fall'))
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map(product => {
                const productPath = `/products/${product.shortName}`;
                // Get annual plan price (lowest price)
                const annualPlan = product.pricingPlans?.find((p: any) => p.billingCycle === 'annual');
                const monthlyPlan = product.pricingPlans?.find((p: any) => p.billingCycle === 'monthly');
                const annualPrice = annualPlan?.monthlyPrice || 0;
                const monthlyPrice = monthlyPlan?.monthlyPrice || 0;
                
                // Get product-specific description
                const getDescription = () => {
                  if (product.shortName === 'ez-care') {
                    return `Medical alert system for seniors. Compare to Life Alert. Get home protection, starting as low as $${annualPrice.toFixed(2)}/month when paying annually.`;
                  }
                  if (product.shortName === 'ez-care-plus') {
                    return `Enhanced medical alert system with cellular connectivity. No landline required. Starting at $${annualPrice.toFixed(2)}/month when paying annually or $${monthlyPrice.toFixed(2)}/month.`;
                  }
                  if (product.shortName === 'lifebeacon') {
                    return `On-the-go wristband alert with GPS. Optional fall detection can automatically call for help and is selected on the product page. Starting at $${annualPrice.toFixed(2)}/month when paying annually or $${monthlyPrice.toFixed(2)}/month.`;
                  }
                  return product.description;
                };
                
                return (
                  <div key={product.id} className="pricing-device-card">
                    <h3 className="pricing-device-name">
                      <Link to={productPath} className="pricing-device-name-link">
                        {product.name}
                      </Link>
                    </h3>
                    <p className="pricing-device-description">
                      {getDescription()}
                    </p>
                    
                    <Link
                      to={productPath}
                      className="pricing-device-image-link"
                      aria-label={`View ${product.name} details`}
                    >
                      <div className="pricing-device-image-wrapper">
                        {productImages[product.shortName] ? (
                          <img src={productImages[product.shortName]} alt={product.name} className="pricing-device-image" />
                        ) : (
                          <div className="pricing-device-placeholder">
                            <svg width="300" height="200" viewBox="0 0 300 200" fill="none">
                              <rect width="300" height="200" fill="#f0f0f0" rx="8"/>
                              <text x="150" y="100" textAnchor="middle" fill="#999" fontSize="16">Product Image</text>
                            </svg>
                          </div>
                        )}
                      </div>
                    </Link>
                    
                    <Link to={productPath} className="pricing-device-button">Learn More</Link>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq-section">
        <div className="pricing-faq-container">
          <h2 className="pricing-faq-title">Frequently Asked Questions</h2>
          
          <div className="pricing-faq-list">
            {/* FAQ 1 */}
            <div className="pricing-faq-item">
              <button 
                className="pricing-faq-question"
                onClick={() => toggleFaq(0)}
              >
                <span>Can I cancel my account at any time?</span>
                <span className="pricing-faq-icon">{openFaqIndex === 0 ? '−' : '+'}</span>
              </button>
              {openFaqIndex === 0 && (
                <div className="pricing-faq-answer">
                  Yes. Alert1 does not require long term contracts; any user may cancel service at any time by returning the Alert1 equipment. If you are on a contract of a specified length (e.g. annual) for a discount, you may cancel early but Alert1 does not offer refunds except in the case of death or a move to a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.
                </div>
              )}
            </div>

            {/* FAQ 2 */}
            <div className="pricing-faq-item">
              <button 
                className="pricing-faq-question"
                onClick={() => toggleFaq(1)}
              >
                <span>What payment types do you accept?</span>
                <span className="pricing-faq-icon">{openFaqIndex === 1 ? '−' : '+'}</span>
              </button>
              {openFaqIndex === 1 && (
                <div className="pricing-faq-answer">
                  We accept all major credit and debit cards.
                </div>
              )}
            </div>

            {/* FAQ 3 */}
            <div className="pricing-faq-item">
              <button 
                className="pricing-faq-question"
                onClick={() => toggleFaq(2)}
              >
                <span>How much do medical alert systems cost?</span>
                <span className="pricing-faq-icon">{openFaqIndex === 2 ? '−' : '+'}</span>
              </button>
              {openFaqIndex === 2 && (
                <div className="pricing-faq-answer">
                  The cost of a medical alert system is between $20 and $60 per month depending upon the features and options selected. Unlike most of our competitors, almost all of our equipment is free to use. We think you'll agree that our systems provide the best value in the industry.
                </div>
              )}
            </div>

            {/* FAQ 4 */}
            <div className="pricing-faq-item">
              <button 
                className="pricing-faq-question"
                onClick={() => toggleFaq(3)}
              >
                <span>Are there any long-term contracts?</span>
                <span className="pricing-faq-icon">{openFaqIndex === 3 ? '−' : '+'}</span>
              </button>
              {openFaqIndex === 3 && (
                <div className="pricing-faq-answer">
                  There are no long-term contracts. You can pay month to month or pre-pay for a year, but no one is locked in.
                </div>
              )}
            </div>

            {/* FAQ 5 */}
            <div className="pricing-faq-item">
              <button 
                className="pricing-faq-question"
                onClick={() => toggleFaq(4)}
              >
                <span>How much does Life Alert cost per month?</span>
                <span className="pricing-faq-icon">{openFaqIndex === 4 ? '−' : '+'}</span>
              </button>
              {openFaqIndex === 4 && (
                <div className="pricing-faq-answer">
                  In most cases, Life Alert costs are much higher than Alert1's-- up to 3 times as much.<sup>1</sup> To get Life Alert's current prices and Life Alert's equipment, you'll need to call them. When it comes to price, service, and quality, we think you'll agree that Alert1 offers the best value in medical alert systems for seniors. There are no hidden fees with an Alert1 system.
                </div>
              )}
            </div>

            {/* FAQ 6 */}
            <div className="pricing-faq-item">
              <button 
                className="pricing-faq-question"
                onClick={() => toggleFaq(5)}
              >
                <span>Where do I find reviews of medical alert systems?</span>
                <span className="pricing-faq-icon">{openFaqIndex === 5 ? '−' : '+'}</span>
              </button>
              {openFaqIndex === 5 && (
                <div className="pricing-faq-answer">
                  Reviews of medical alert systems are available on the Alert1 site.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Consumer Affairs Section */}
      <section className="pricing-consumer-affairs-section">
        <div className="pricing-consumer-affairs-container">
          {consumerAffairsLogo ? (
            <img 
              src={consumerAffairsLogo} 
              alt="Consumer Affairs Rating" 
              className="consumer-affairs-logo-image"
            />
          ) : (
            <>
              <div className="consumer-affairs-stars">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#4a4a4a">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#4a4a4a">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#4a4a4a">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#4a4a4a">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="#4a4a4a">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                </svg>
              </div>
              
              <h2 className="consumer-affairs-logo">
                <span className="consumer-text">Consumer</span>
                <span className="affairs-text">Affairs</span>
              </h2>
            </>
          )}
          
          <p className="consumer-affairs-text">
            We also want you to know that we have a high Overall Satisfaction Rating from Consumer Affairs. From our service to the cost of medical alert systems, you can rest assured that Alert1 has your best interests at heart.
          </p>
        </div>
      </section>

      {/* Refund Policy Section */}
      <section className="pricing-refund-section">
        <div className="pricing-refund-container">
          <h2 className="pricing-refund-title">Refund Policy</h2>
          
          <p className="pricing-refund-text">
            Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.
          </p>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="pricing-cta-section">
        <div className="pricing-cta-container">
          <p className="pricing-refund-footnote">
            <sup>1</sup>As of February 2025
          </p>
          <p className="pricing-refund-footnote">
            <sup>2</sup>See refund policy above.
          </p>
          
          <a href="tel:1-877-579-9862" className="pricing-call-today-btn" style={{ display: 'inline-block', textDecoration: 'none' }}>CALL US TODAY!</a>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PricingPage;
